import { Facility, FleetVehicle, validPosition } from "./fleetData";

export interface FleetEndpoint { name: string; lat: number; lng: number; kind: "source" | "destination" | "base"; }

function tripEndpoint(vehicle: FleetVehicle, facilities: Facility[], kind: "source" | "destination"): FleetEndpoint | undefined {
  const name = vehicle[kind];
  const position = vehicle[kind === "source" ? "sourcePosition" : "destinationPosition"];
  if (position && validPosition(position.lat, position.lng)) return { ...position, name: name || (kind === "source" ? "Trip source" : "Trip destination"), kind };
  // Only match unambiguous exact names; never guess a nearby facility.
  const matches = name ? facilities.filter(f => f.name.trim().toLowerCase() === name.trim().toLowerCase()) : [];
  if (matches.length === 1) return { ...matches[0], kind };
}

export function fleetDestination(vehicle: FleetVehicle, facilities: Facility[]): FleetEndpoint | undefined {
  if (vehicle.onTrip === true || vehicle.tripId != null) return tripEndpoint(vehicle, facilities, "destination");
  if (vehicle.onTrip !== false || vehicle.baseStation?.isActive === false) return;
  const base = vehicle.baseStation;
  const facility = base ? facilities.find(item => item.id === String(base.id)) : undefined;
  const position = validPosition(base?.latitude, base?.longitude) ?? (facility && validPosition(facility.lat, facility.lng));
  if (base && position) return { ...position, name: base.name, kind: "base" };
}

// Use radial GPS distance, not road distance, for the 500 m arrival area.
export function fleetAtBase(vehicle: FleetVehicle, facilities: Facility[]): boolean {
  const base = fleetDestination(vehicle, facilities);
  const point = validPosition(vehicle.lat, vehicle.lng);
  if (base?.kind !== "base" || !point) return false;
  const radians = Math.PI / 180;
  const dLat = (base.lat - point.lat) * radians;
  const dLng = (base.lng - point.lng) * radians;
  const a = Math.sin(dLat / 2) ** 2 + Math.cos(point.lat * radians) * Math.cos(base.lat * radians) * Math.sin(dLng / 2) ** 2;
  const metres = 6371000 * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(Math.max(0, 1 - a)));
  // Allow sub-millimetre rounding at the inclusive boundary.
  return metres <= 500 + 1e-6;
}

export function fleetRouteEndpoints(vehicle: FleetVehicle, facilities: Facility[]): [FleetEndpoint, FleetEndpoint] | undefined {
  const destination = fleetDestination(vehicle, facilities);
  if (!destination || (destination.kind === "base" && fleetAtBase(vehicle, facilities))) return;
  const source = destination.kind === "destination" ? tripEndpoint(vehicle, facilities, "source") : validPosition(vehicle.lat, vehicle.lng);
  if (source) return [{ ...source, name: "name" in source && typeof source.name === "string" ? source.name : vehicle.plate, kind: "source" }, destination];
}
